"use client";

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  ChevronRight,
  GripVertical,
  Info,
  Eye,
  Pencil,
  Plus,
  ScrollText,
  Search,
  Tags,
  Trash2,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Agent } from "@/lib/api";
import { DEFAULT_AGENT_PROMPTS } from "@/lib/api";
import { AgentMarkdownEditor } from "@/components/agent-markdown-editor";
import { AgentPromptEditDialog } from "@/components/agent-prompt-edit-dialog";
import { Select } from "@/components/ui/select";
import { AUDIOSET_LABELS } from "@/lib/audioset-labels";
import {
  ACOUSTIC_EVENTS_DEFAULT_POLICIES,
  ACOUSTIC_EVENTS_COOLDOWN_DEFAULT,
  ACOUSTIC_EVENTS_THRESHOLD_DEFAULT,
  ACOUSTIC_EVENTS_THRESHOLD_MAX,
  ACOUSTIC_EVENTS_THRESHOLD_MIN,
  ACOUSTIC_EVENT_MODE_OPTIONS,
  LIVE_PERCEPTION_DEFAULTS,
  LIVE_PERCEPTION_LANGUAGE_OPTIONS,
  LIVE_PERCEPTION_VIDEO_OPTIONS,
  LIVE_PERCEPTION_VOICE_OPTIONS,
  acousticPolicyLabels,
  clampAcousticCooldownSec,
  clampAcousticEventsThreshold,
  hintFor,
  normalizeAcousticEventPolicies,
  type AcousticEventMode,
  type AcousticEventPolicy,
} from "@/lib/live-perception-settings";
import {
  TTS_EMOTION_MORE,
  TTS_EMOTION_PRIMARY,
} from "@/lib/voice-catalog";
import {
  DEFAULT_SELECTED_CHIP_SLUGS,
  PERCEPTION_CHIP_KINDS,
  STOCK_PERCEPTION_CHIPS,
  parsePerceptionChips,
  parsePerceptionRules,
  serializePerceptionChips,
  serializePerceptionRules,
  slugifyChipLabel,
  type PerceptionChipDef,
  type PerceptionChipKind,
  type PerceptionRule,
} from "@/lib/perception-chips";

const FOCUS_MAX = 2000;
const RULE_MAX = 280;
const AFFECT_MAX = 2000;

type PercTab = "focus" | "rules" | "chips";

type Props = {
  draft: Agent;
  onChange: (next: Agent) => void;
  /** Direct: acoustic Respond modes are not applied in v1. */
  agentMode?: "cascade" | "direct" | "liveavatar";
};

function ensureChipState(raw: string | undefined | null) {
  const parsed = parsePerceptionChips(raw);
  if (!String(raw || "").trim()) {
    return {
      selected: new Set(DEFAULT_SELECTED_CHIP_SLUGS),
      custom: [] as PerceptionChipDef[],
    };
  }
  return parsed;
}

function ensureRules(raw: string | undefined | null): PerceptionRule[] {
  const parsed = parsePerceptionRules(raw);
  if (parsed.length) return parsed;
  const defaults = String(DEFAULT_AGENT_PROMPTS.videoPerceptionRules || "")
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (defaults.length <= 1) {
    return [
      { id: "r1", text: "Describe only visible, observable cues.", enabled: true },
      { id: "r2", text: "Use neutral, non-judgmental language.", enabled: true },
      { id: "r3", text: "Do not infer health status, identity, or intent.", enabled: true },
    ];
  }
  return defaults.map((text, i) => ({ id: `r${i}`, text, enabled: true }));
}

export function AgentEditPerceptionPanel({
  draft,
  onChange,
  agentMode = "cascade",
}: Props) {
  const [tab, setTab] = useState<PercTab>("focus");
  const [affectOpen, setAffectOpen] = useState(false);
  const [chipSearch, setChipSearch] = useState("");
  const [addChipOpen, setAddChipOpen] = useState(false);
  const [newChipName, setNewChipName] = useState("");
  const [newChipKind, setNewChipKind] = useState<PerceptionChipKind>("expression");
  const [newChipDesc, setNewChipDesc] = useState("");
  const [ruleEdit, setRuleEdit] = useState<{ id: string | null; text: string } | null>(null);
  const [ruleDeleteId, setRuleDeleteId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [acousticSearch, setAcousticSearch] = useState("");
  const [acousticEditLabel, setAcousticEditLabel] = useState<string | null>(null);

  const focus = draft.videoPerceptionFocus ?? DEFAULT_AGENT_PROMPTS.videoPerceptionFocus;
  const affect = draft.affectPrompt ?? DEFAULT_AGENT_PROMPTS.affectPrompt;
  const isDirect = agentMode === "direct" || agentMode === "liveavatar";
  const acousticModeOptions = isDirect
    ? ACOUSTIC_EVENT_MODE_OPTIONS.filter((o) => o.value === "observe" || o.value === "context")
    : ACOUSTIC_EVENT_MODE_OPTIONS;
  const chipState = useMemo(
    () => ensureChipState(draft.videoPerceptionChips),
    [draft.videoPerceptionChips],
  );
  const rules = useMemo(
    () => ensureRules(draft.videoPerceptionRules),
    [draft.videoPerceptionRules],
  );

  const catalog = useMemo(() => {
    const bySlug = new Map<string, PerceptionChipDef>();
    for (const c of STOCK_PERCEPTION_CHIPS) bySlug.set(c.slug, c);
    for (const c of chipState.custom) bySlug.set(c.slug, c);
    return [...bySlug.values()];
  }, [chipState.custom]);

  const q = chipSearch.trim().toLowerCase();
  const filteredByKind = useMemo(() => {
    return PERCEPTION_CHIP_KINDS.map((kind) => {
      const chips = catalog.filter(
        (c) =>
          c.kind === kind.id &&
          (!q ||
            c.label.toLowerCase().includes(q) ||
            c.slug.includes(q) ||
            (c.description || "").toLowerCase().includes(q)),
      );
      return { ...kind, chips };
    }).filter((g) => g.chips.length > 0);
  }, [catalog, q]);

  const selectedCount = chipState.selected.size;
  const totalCount = catalog.length;

  const acousticPolicies = useMemo(() => {
    const fromPolicies = draft.livePerceptionAcousticEventsPolicies;
    if (Array.isArray(fromPolicies) && fromPolicies.length) {
      return normalizeAcousticEventPolicies(fromPolicies);
    }
    const raw = draft.livePerceptionAcousticEventsWhitelist;
    if (Array.isArray(raw) && raw.length) {
      return normalizeAcousticEventPolicies(raw);
    }
    return normalizeAcousticEventPolicies(fromPolicies || []);
  }, [draft.livePerceptionAcousticEventsPolicies, draft.livePerceptionAcousticEventsWhitelist]);

  const acousticSelected = useMemo(
    () => new Set(acousticPolicies.map((p) => p.label)),
    [acousticPolicies],
  );

  const acousticFiltered = useMemo(() => {
    const qq = acousticSearch.trim().toLowerCase();
    if (!qq) return AUDIOSET_LABELS as unknown as string[];
    return (AUDIOSET_LABELS as unknown as string[]).filter((label) =>
      label.toLowerCase().includes(qq),
    );
  }, [acousticSearch]);

  const acousticThreshold = clampAcousticEventsThreshold(
    draft.livePerceptionAcousticEventsThreshold ?? ACOUSTIC_EVENTS_THRESHOLD_DEFAULT,
  );

  const acousticEditing = useMemo(() => {
    if (!acousticEditLabel) return null;
    return acousticPolicies.find((p) => p.label === acousticEditLabel) || null;
  }, [acousticEditLabel, acousticPolicies]);

  function patch(partial: Partial<Agent>) {
    onChange({ ...draft, ...partial });
  }

  function writeAcousticPolicies(next: AcousticEventPolicy[]) {
    const normalized = normalizeAcousticEventPolicies(next);
    patch({
      livePerceptionAcousticEventsPolicies: normalized,
      livePerceptionAcousticEventsWhitelist: acousticPolicyLabels(normalized),
    });
  }

  function toggleAcousticLabel(label: string) {
    const map = new Map(acousticPolicies.map((p) => [p.label, p]));
    if (map.has(label)) {
      map.delete(label);
      if (acousticEditLabel === label) setAcousticEditLabel(null);
    } else {
      map.set(label, {
        label,
        mode: "observe",
        cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT,
      });
      setAcousticEditLabel(label);
    }
    writeAcousticPolicies(
      (AUDIOSET_LABELS as unknown as string[])
        .filter((name) => map.has(name))
        .map((name) => map.get(name)!),
    );
  }

  function updateAcousticPolicy(label: string, patchRow: Partial<AcousticEventPolicy>) {
    writeAcousticPolicies(
      acousticPolicies.map((p) => (p.label === label ? { ...p, ...patchRow, label } : p)),
    );
  }

  function modeLabel(mode: AcousticEventMode) {
    return ACOUSTIC_EVENT_MODE_OPTIONS.find((o) => o.value === mode)?.label || mode;
  }

  function writeChips(selected: Set<string>, custom: PerceptionChipDef[]) {
    patch({ videoPerceptionChips: serializePerceptionChips(selected, custom) });
  }

  function writeRules(next: PerceptionRule[]) {
    patch({ videoPerceptionRules: serializePerceptionRules(next) });
  }

  function toggleChip(slug: string) {
    const next = new Set(chipState.selected);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    writeChips(next, chipState.custom);
  }

  function selectAllVisible() {
    const next = new Set(chipState.selected);
    for (const g of filteredByKind) for (const c of g.chips) next.add(c.slug);
    writeChips(next, chipState.custom);
  }

  function clearAllVisible() {
    const next = new Set(chipState.selected);
    for (const g of filteredByKind) for (const c of g.chips) next.delete(c.slug);
    writeChips(next, chipState.custom);
  }

  function addCustomChip() {
    const label = newChipName.trim();
    if (!label) return;
    let slug = slugifyChipLabel(label);
    const existing = new Set(catalog.map((c) => c.slug));
    if (existing.has(slug)) {
      let n = 2;
      while (existing.has(`${slug}-${n}`)) n += 1;
      slug = `${slug}-${n}`;
    }
    const custom: PerceptionChipDef[] = [
      ...chipState.custom,
      {
        slug,
        label,
        kind: newChipKind,
        description: newChipDesc.trim() || undefined,
        custom: true,
      },
    ];
    const selected = new Set(chipState.selected);
    selected.add(slug);
    writeChips(selected, custom);
    setAddChipOpen(false);
    setNewChipName("");
    setNewChipDesc("");
    setNewChipKind("expression");
  }

  function onDropRule(targetId: string) {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const from = rules.findIndex((r) => r.id === dragId);
    const to = rules.findIndex((r) => r.id === targetId);
    if (from < 0 || to < 0) {
      setDragId(null);
      return;
    }
    const next = [...rules];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    writeRules(next);
    setDragId(null);
  }

  return (
    <div className="agent-conv agent-perc">
      <section className="agent-perc-block">
        <header className="agent-perc-block-head">
          <h3 className="agent-conv-h">Signals</h3>
          <p className="agent-limits-lead">
            Real-time analysis during the conversation. Signals are available for prompting and call
            flow.
          </p>
        </header>
        <div className="agent-perc-live">
          {agentMode === "liveavatar" ? (
            <div className="agent-perc-live-row">
              <div>
                <div className="agent-perc-signal-title">
                  <b>Client session archive (FULL)</b>
                  <span className="agent-prompt-optional">During the call</span>
                </div>
                <span>
                  Records mic, camera, and avatar tracks in the browser to the same Admin
                  archive slots as Track Egress. Needed because LiveAvatar FULL media never
                  hits Pulse LiveKit. Off by default.
                </span>
              </div>
              <Switch
                checked={draft.clientSessionArchiveEnabled === true}
                onCheckedChange={(v) => patch({ clientSessionArchiveEnabled: v })}
                aria-label="Enable client session archive for LiveAvatar FULL"
              />
            </div>
          ) : null}
          <div className="agent-perc-live-row">
            <div>
              <div className="agent-perc-signal-title">
                <b>Live Perception</b>
                <span className="agent-prompt-optional">During the call</span>
              </div>
              <span>
                Silent LiveKit participant for fuse ticks. Acoustic events can drive bot
                reactions per policy. Off by default.
              </span>
            </div>
            <Switch
              checked={draft.livePerceptionEnabled === true}
              onCheckedChange={(v) => patch({ livePerceptionEnabled: v })}
              aria-label="Enable Live Perception"
            />
          </div>
          {draft.livePerceptionEnabled === true ? (
            <div className="agent-perc-live-fields">
              <label className="agent-perc-live-field">
                <span>Language</span>
                <Select
                  value={draft.livePerceptionLanguageModel || LIVE_PERCEPTION_DEFAULTS.livePerceptionLanguageModel}
                  onChange={(v) => patch({ livePerceptionLanguageModel: v || LIVE_PERCEPTION_DEFAULTS.livePerceptionLanguageModel })}
                  options={LIVE_PERCEPTION_LANGUAGE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                />
                <em>
                  {hintFor(
                    LIVE_PERCEPTION_LANGUAGE_OPTIONS,
                    draft.livePerceptionLanguageModel || LIVE_PERCEPTION_DEFAULTS.livePerceptionLanguageModel,
                    "",
                  )}
                </em>
              </label>
              <label className="agent-perc-live-field">
                <span>Voice</span>
                <Select
                  value={draft.livePerceptionVoiceMode || LIVE_PERCEPTION_DEFAULTS.livePerceptionVoiceMode}
                  onChange={(v) => patch({ livePerceptionVoiceMode: v || LIVE_PERCEPTION_DEFAULTS.livePerceptionVoiceMode })}
                  options={LIVE_PERCEPTION_VOICE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                />
                <em>
                  {hintFor(
                    LIVE_PERCEPTION_VOICE_OPTIONS,
                    draft.livePerceptionVoiceMode || LIVE_PERCEPTION_DEFAULTS.livePerceptionVoiceMode,
                    "",
                  )}
                </em>
              </label>
              <label className="agent-perc-live-field">
                <span>Video</span>
                <Select
                  value={draft.livePerceptionVideoModel || LIVE_PERCEPTION_DEFAULTS.livePerceptionVideoModel}
                  onChange={(v) => patch({ livePerceptionVideoModel: v || LIVE_PERCEPTION_DEFAULTS.livePerceptionVideoModel })}
                  options={LIVE_PERCEPTION_VIDEO_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                />
                <em>
                  {hintFor(
                    LIVE_PERCEPTION_VIDEO_OPTIONS,
                    draft.livePerceptionVideoModel || LIVE_PERCEPTION_DEFAULTS.livePerceptionVideoModel,
                    "",
                  )}
                </em>
              </label>
              <div className="agent-perc-live-row">
                <div>
                  <div className="agent-perc-signal-title">
                    <b>Show debug frames in transcript</b>
                  </div>
                  <span>Admin session detail and Conversation when signed in as admin only.</span>
                </div>
                <Switch
                  checked={draft.livePerceptionDebugFramesInTranscript === true}
                  onCheckedChange={(v) => patch({ livePerceptionDebugFramesInTranscript: v })}
                  aria-label="Show Live Perception debug frames in transcript"
                />
              </div>
              <div className="agent-perc-live-row">
                <div>
                  <div className="agent-perc-signal-title">
                    <b>Acoustic events</b>
                    <span className="agent-prompt-optional">CED-tiny · per-label policy</span>
                  </div>
                  <span>
                    Tag non-speech sounds on the mic (knock, laughter, flush, …). Selected labels
                    are detected by Live Perception; each label has a bot policy. Off by default.
                    {isDirect ? " Direct v1 applies Log only and Context; Respond is later." : ""}
                  </span>
                </div>
                <Switch
                  checked={draft.livePerceptionAcousticEventsEnabled === true}
                  onCheckedChange={(v) => {
                    if (v !== true) {
                      patch({ livePerceptionAcousticEventsEnabled: false });
                      setAcousticEditLabel(null);
                      return;
                    }
                    const next: Partial<Agent> = { livePerceptionAcousticEventsEnabled: true };
                    if (acousticPolicies.length === 0) {
                      const seeded = normalizeAcousticEventPolicies(ACOUSTIC_EVENTS_DEFAULT_POLICIES).map(
                        (p) =>
                          isDirect && p.mode !== "observe" && p.mode !== "context"
                            ? { ...p, mode: "observe" as AcousticEventMode, ttsEmotion: null, insertNonverbal: null }
                            : p,
                      );
                      next.livePerceptionAcousticEventsPolicies = seeded;
                      next.livePerceptionAcousticEventsWhitelist = acousticPolicyLabels(seeded);
                    }
                    patch(next);
                  }}
                  aria-label="Enable Live Perception acoustic events"
                />
              </div>
              {draft.livePerceptionAcousticEventsEnabled === true ? (
                <div className="agent-perc-acoustic">
                  <label className="agent-perc-live-field">
                    <span>Default min score</span>
                    <input
                      type="number"
                      min={ACOUSTIC_EVENTS_THRESHOLD_MIN}
                      max={ACOUSTIC_EVENTS_THRESHOLD_MAX}
                      step={0.01}
                      value={acousticThreshold}
                      onChange={(e) =>
                        patch({
                          livePerceptionAcousticEventsThreshold: clampAcousticEventsThreshold(
                            e.target.value,
                          ),
                        })
                      }
                      aria-label="Default acoustic score threshold"
                    />
                    <em>Applies to sounds without their own min (edit a pill to override).</em>
                  </label>
                  <div className="agent-perc-live-field">
                    <span>
                      Sounds <em>{acousticPolicies.length} selected</em>
                    </span>
                    <em>
                      Safe defaults on first enable. Click a pill to edit; right-click to remove.
                    </em>
                    {acousticPolicies.length ? (
                      <div className="agent-perc-acoustic-selected" aria-label="Selected sounds">
                        {acousticPolicies.map((p) => (
                          <button
                            key={p.label}
                            type="button"
                            className={`agent-perc-acoustic-pill is-on${
                              acousticEditLabel === p.label ? " is-editing" : ""
                            }`}
                            onClick={() =>
                              setAcousticEditLabel(
                                acousticEditLabel === p.label ? null : p.label,
                              )
                            }
                            onContextMenu={(e) => {
                              e.preventDefault();
                              toggleAcousticLabel(p.label);
                            }}
                            title={`${p.label} · ${modeLabel(p.mode)}${
                              p.threshold != null ? ` · ≥${p.threshold}` : ""
                            } — click to edit, right-click to remove`}
                          >
                            {p.label}
                            <span className="agent-perc-acoustic-pill-mode">
                              {modeLabel(p.mode)}
                            </span>
                            {p.threshold != null ? (
                              <span className="agent-perc-acoustic-pill-thr">
                                ≥{Number(p.threshold).toFixed(2)}
                              </span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                    {acousticEditing ? (
                      <div
                        className="agent-perc-acoustic-editor"
                        aria-label={`Policy for ${acousticEditing.label}`}
                      >
                        <div className="agent-perc-acoustic-editor-head">
                          <b>{acousticEditing.label}</b>
                          <button
                            type="button"
                            onClick={() => toggleAcousticLabel(acousticEditing.label)}
                          >
                            Remove
                          </button>
                        </div>
                        <label className="agent-perc-live-field">
                          <span>Mode</span>
                          <Select
                            value={acousticEditing.mode}
                            onChange={(v) =>
                              updateAcousticPolicy(acousticEditing.label, {
                                mode: (v as AcousticEventMode) || "observe",
                              })
                            }
                            options={acousticModeOptions.map((o) => ({
                              value: o.value,
                              label: o.label,
                            }))}
                            ariaLabel={`Mode for ${acousticEditing.label}`}
                          />
                          <em>
                            {
                              acousticModeOptions.find(
                                (o) => o.value === acousticEditing.mode,
                              )?.hint
                            }
                          </em>
                        </label>
                        <div className="agent-perc-acoustic-editor-row">
                          <label className="agent-perc-live-field">
                            <span>Cooldown (sec)</span>
                            <input
                              type="number"
                              min={3}
                              max={120}
                              step={1}
                              value={clampAcousticCooldownSec(acousticEditing.cooldownSec)}
                              onChange={(e) =>
                                updateAcousticPolicy(acousticEditing.label, {
                                  cooldownSec: clampAcousticCooldownSec(e.target.value),
                                })
                              }
                              aria-label={`Cooldown for ${acousticEditing.label}`}
                            />
                          </label>
                          <label className="agent-perc-live-field">
                            <span>Min score</span>
                            <input
                              type="number"
                              min={ACOUSTIC_EVENTS_THRESHOLD_MIN}
                              max={ACOUSTIC_EVENTS_THRESHOLD_MAX}
                              step={0.01}
                              value={
                                acousticEditing.threshold != null
                                  ? acousticEditing.threshold
                                  : ""
                              }
                              placeholder={String(acousticThreshold)}
                              onChange={(e) => {
                                const raw = e.target.value.trim();
                                if (raw === "") {
                                  writeAcousticPolicies(
                                    acousticPolicies.map((p) => {
                                      if (p.label !== acousticEditing.label) return p;
                                      const next = { ...p };
                                      delete next.threshold;
                                      return next;
                                    }),
                                  );
                                  return;
                                }
                                updateAcousticPolicy(acousticEditing.label, {
                                  threshold: clampAcousticEventsThreshold(raw),
                                });
                              }}
                              aria-label={`Min score for ${acousticEditing.label}`}
                            />
                          </label>
                        </div>
                        {acousticEditing.mode !== "observe" ? (
                          <label className="agent-perc-live-field">
                            <span>Instruction (LLM)</span>
                            <textarea
                              rows={3}
                              value={acousticEditing.instruction || ""}
                              onChange={(e) =>
                                updateAcousticPolicy(acousticEditing.label, {
                                  instruction: e.target.value.slice(0, 2000),
                                })
                              }
                              placeholder="How the bot should react when this sound is heard…"
                              aria-label={`Instruction for ${acousticEditing.label}`}
                            />
                          </label>
                        ) : null}
                        {acousticEditing.mode === "respond_if_idle" ||
                        acousticEditing.mode === "respond" ? (
                          <>
                            <label className="agent-perc-live-field">
                              <span>TTS emotion</span>
                              <Select
                                value={acousticEditing.ttsEmotion || ""}
                                onChange={(v) =>
                                  updateAcousticPolicy(acousticEditing.label, {
                                    ttsEmotion: v || null,
                                  })
                                }
                                options={[
                                  { value: "", label: "Default (agent voice)" },
                                  ...TTS_EMOTION_PRIMARY.map((em) => ({
                                    value: em,
                                    label: em,
                                  })),
                                  ...TTS_EMOTION_MORE.map((em) => ({
                                    value: em,
                                    label: em,
                                  })),
                                ]}
                                ariaLabel={`TTS emotion for ${acousticEditing.label}`}
                              />
                            </label>
                            <div className="agent-perc-acoustic-nonverbal">
                              <label className="agent-perc-acoustic-nonverbal-check">
                                <input
                                  type="checkbox"
                                  checked={Boolean(acousticEditing.insertNonverbal)}
                                  onChange={(e) =>
                                    updateAcousticPolicy(acousticEditing.label, {
                                      insertNonverbal: e.target.checked
                                        ? acousticEditing.insertNonverbal || "[laughter]"
                                        : null,
                                    })
                                  }
                                  aria-label={`Insert nonverbal token for ${acousticEditing.label}`}
                                />
                                <span>Insert nonverbal in TTS</span>
                              </label>
                              {acousticEditing.insertNonverbal ? (
                                <label className="agent-perc-live-field">
                                  <span>Token (Cartesia / provider)</span>
                                  <input
                                    type="text"
                                    value={acousticEditing.insertNonverbal}
                                    onChange={(e) =>
                                      updateAcousticPolicy(acousticEditing.label, {
                                        insertNonverbal: e.target.value.slice(0, 40) || "[laughter]",
                                      })
                                    }
                                    placeholder="[laughter]"
                                    aria-label={`Nonverbal token for ${acousticEditing.label}`}
                                  />
                                  <em>
                                    Prepended to the spoken line if missing (e.g. Cartesia{" "}
                                    <code>[laughter]</code>).
                                  </em>
                                </label>
                              ) : null}
                            </div>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="agent-perc-chips-toolbar">
                      <label className="agent-perc-search">
                        <Search size={14} aria-hidden />
                        <input
                          value={acousticSearch}
                          onChange={(e) => setAcousticSearch(e.target.value)}
                          placeholder="Search sounds (knock, laughter, flush…)"
                          aria-label="Search AudioSet labels"
                        />
                      </label>
                      <div className="agent-perc-chips-actions">
                        <button
                          type="button"
                          onClick={() => {
                            const map = new Map(acousticPolicies.map((p) => [p.label, p]));
                            for (const label of acousticFiltered.slice(0, 40)) {
                              if (!map.has(label)) {
                                map.set(label, {
                                  label,
                                  mode: "observe",
                                  cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT,
                                });
                              }
                            }
                            writeAcousticPolicies(
                              (AUDIOSET_LABELS as unknown as string[])
                                .filter((name) => map.has(name))
                                .map((name) => map.get(name)!),
                            );
                          }}
                          disabled={!acousticFiltered.length}
                        >
                          Add visible
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            writeAcousticPolicies([]);
                            setAcousticEditLabel(null);
                          }}
                          disabled={!acousticPolicies.length}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="agent-perc-acoustic-list" role="listbox" aria-label="AudioSet labels">
                      {acousticFiltered.slice(0, 80).map((label) => {
                        const on = acousticSelected.has(label);
                        return (
                          <button
                            key={label}
                            type="button"
                            role="option"
                            aria-selected={on}
                            className={`agent-perc-chip${on ? " is-on" : ""}`}
                            onClick={() => toggleAcousticLabel(label)}
                          >
                            <span className="agent-perc-chip-mark" aria-hidden>
                              {on ? <Check size={12} /> : null}
                            </span>
                            {label}
                          </button>
                        );
                      })}
                      {acousticFiltered.length > 80 ? (
                        <p className="agent-limits-lead">
                          Showing 80 of {acousticFiltered.length} matches — refine the search.
                        </p>
                      ) : null}
                      {!acousticFiltered.length ? (
                        <p className="agent-limits-lead">No labels match that search.</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className="agent-perc-signal-card"
          onClick={() => setAffectOpen(true)}
        >
          <div className="agent-perc-signal-copy">
            <div className="agent-perc-signal-title">
              <b>Affect classifier</b>
              <span className="agent-prompt-optional">Optional</span>
            </div>
            <span>Labels affect for the current user turn.</span>
          </div>
          <span className="agent-perc-signal-action">
            Edit prompt
            <ChevronRight size={16} aria-hidden />
          </span>
        </button>
      </section>

      <section className="agent-perc-block">
        <header className="agent-perc-block-head agent-perc-block-head--row">
          <div>
            <div className="agent-perc-title-row">
              <h3 className="agent-conv-h">Perception</h3>
              <span className="agent-perc-after-badge">After the call</span>
            </div>
          </div>
        </header>

        <div className="agent-limits-note" role="note">
          <Info size={15} strokeWidth={2} aria-hidden />
          <p>
            Runs after hangup on the webcam recording. Provider and model are configured in{" "}
            <Link to="/settings" className="agent-eot-nudge-link">
              Settings → AI providers → After the call
            </Link>
            .
          </p>
        </div>

        <div className="agent-perc-iprof-toggles" style={{ marginBottom: 16 }}>
          <p className="agent-perc-signal-copy" style={{ marginBottom: 10 }}>
            <b>Interaction Profile report</b>
            <span className="muted" style={{ display: "block", fontWeight: 400, marginTop: 4 }}>
              Layers included in Open analysis after hangup (not live bot reactions).
            </span>
          </p>
          <label className="agent-perc-row-check">
            <Switch
              checked={draft.interactionProfileOfflineVoiceEnabled !== false}
              onCheckedChange={(v) => patch({ interactionProfileOfflineVoiceEnabled: v })}
              aria-label="Include offline voice in Interaction Profile"
            />
            <span>Offline voice (SER from user audio)</span>
          </label>
          <label className="agent-perc-row-check">
            <Switch
              checked={
                draft.interactionProfileAcousticsEnabled === true
                || (draft.interactionProfileAcousticsEnabled !== false
                  && acousticPolicies.length > 0)
              }
              onCheckedChange={(v) => patch({ interactionProfileAcousticsEnabled: v })}
              aria-label="Include acoustics in Interaction Profile"
            />
            <span>
              Acoustic events in report
              {acousticPolicies.length ? (
                <em style={{ fontWeight: 400, marginLeft: 6 }}>
                  (uses selected sounds + thresholds above)
                </em>
              ) : (
                <em style={{ fontWeight: 400, marginLeft: 6 }}>
                  (enable sounds under Live Perception or turn on to scan offline)
                </em>
              )}
            </span>
          </label>
        </div>

        <div className="agent-perc-tabs" role="tablist" aria-label="Perception sections">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "focus"}
            className={tab === "focus" ? "is-active" : undefined}
            onClick={() => setTab("focus")}
          >
            <Eye size={14} aria-hidden />
            What to notice
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "rules"}
            className={tab === "rules" ? "is-active" : undefined}
            onClick={() => setTab("rules")}
          >
            <ScrollText size={14} aria-hidden />
            Rules
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "chips"}
            className={tab === "chips" ? "is-active" : undefined}
            onClick={() => setTab("chips")}
          >
            <Tags size={14} aria-hidden />
            Chip vocabulary
            <em>{totalCount}</em>
          </button>
        </div>

        {tab === "focus" ? (
          <div className="agent-perc-panel">
            <AgentMarkdownEditor
              value={focus}
              onChange={(v) => patch({ videoPerceptionFocus: v })}
              max={FOCUS_MAX}
              label="Instructions"
              emptyHint="Describe what the model should observe in the webcam recording."
            />
          </div>
        ) : null}

        {tab === "rules" ? (
          <div className="agent-perc-panel">
            <div className="agent-perc-rules-head">
              <p className="agent-limits-lead">
                Add agent-specific guidance for how visual observations should be written.
              </p>
              <button
                type="button"
                className="agent-eot-add"
                onClick={() => setRuleEdit({ id: null, text: "" })}
              >
                <Plus size={14} aria-hidden />
                Add rule
              </button>
            </div>
            <ul className="agent-perc-rules">
              {rules.map((r) => (
                <li
                  key={r.id}
                  className={!r.enabled ? "is-off" : undefined}
                  draggable
                  onDragStart={() => setDragId(r.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropRule(r.id)}
                >
                  <span className="agent-perc-rule-grip" aria-hidden>
                    <GripVertical size={14} />
                  </span>
                  <Switch
                    checked={r.enabled}
                    onCheckedChange={(v) =>
                      writeRules(rules.map((x) => (x.id === r.id ? { ...x, enabled: v } : x)))
                    }
                    aria-label={`Enable rule: ${r.text}`}
                  />
                  <span className="agent-perc-rule-text">{r.text}</span>
                  <button
                    type="button"
                    className="agent-perc-rule-icon"
                    aria-label="Edit rule"
                    onClick={() => setRuleEdit({ id: r.id, text: r.text })}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    className="agent-perc-rule-icon agent-perc-rule-icon--danger"
                    aria-label="Delete rule"
                    onClick={() => setRuleDeleteId(r.id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
            <p className="agent-perc-foot-hint">
              Rules are applied together with the instructions in What to notice.
            </p>
          </div>
        ) : null}

        {tab === "chips" ? (
          <div className="agent-perc-panel">
            <div className="agent-perc-chips-toolbar">
              <label className="agent-perc-search">
                <Search size={14} aria-hidden />
                <input
                  value={chipSearch}
                  onChange={(e) => setChipSearch(e.target.value)}
                  placeholder="Search chips"
                  aria-label="Search chips"
                />
              </label>
              <button
                type="button"
                className="agent-eot-add"
                onClick={() => setAddChipOpen(true)}
              >
                <Plus size={14} aria-hidden />
                Add custom chip
              </button>
              <div className="agent-perc-chips-actions">
                <button type="button" onClick={selectAllVisible}>
                  Select all
                </button>
                <button type="button" onClick={clearAllVisible}>
                  Clear all
                </button>
                <span>
                  {selectedCount} of {totalCount} selected
                </span>
              </div>
            </div>

            <div className="agent-perc-chip-groups">
              {filteredByKind.map((g) => (
                <div key={g.id} className="agent-perc-chip-group">
                  <h4>
                    {g.label} <em>({g.chips.length})</em>
                  </h4>
                  <div className="agent-perc-chip-list">
                    {g.chips.map((c) => {
                      const on = chipState.selected.has(c.slug);
                      return (
                        <button
                          key={c.slug}
                          type="button"
                          className={`agent-perc-chip${on ? " is-on" : ""}`}
                          aria-pressed={on}
                          onClick={() => toggleChip(c.slug)}
                          title={c.description || c.slug}
                        >
                          <span className="agent-perc-chip-mark" aria-hidden>
                            {on ? <Check size={12} strokeWidth={2.5} /> : null}
                          </span>
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <p className="agent-perc-foot-hint">
              Only selected chips are available to the model when creating observations.
            </p>
          </div>
        ) : null}
      </section>

      <AgentPromptEditDialog
        open={affectOpen}
        onOpenChange={setAffectOpen}
        title="Edit affect classifier prompt"
        description="Define how the model identifies the user's observable affect for the current turn."
        banner="This prompt runs in real time and can influence agent responses and call flow."
        optional
        value={affect}
        max={AFFECT_MAX}
        defaultValue={DEFAULT_AGENT_PROMPTS.affectPrompt}
        onSave={(next) => patch({ affectPrompt: next })}
      />

      <Dialog open={addChipOpen} onOpenChange={setAddChipOpen}>
        <DialogContent className="agent-perc-chip-dialog bg-white sm:max-w-md" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className="agent-perc-chip-dialog-title">
              <Tags size={16} aria-hidden />
              Add custom chip
            </DialogTitle>
            <DialogDescription>
              Create a label the model can attach to visual observations.
            </DialogDescription>
          </DialogHeader>
          <div className="agent-perc-chip-form">
            <label className="agent-conv-field">
              <div className="agent-conv-field-head">
                <div>
                  <b>
                    Chip name <i className="agent-perc-req">*</i>
                  </b>
                </div>
              </div>
              <input
                className="agent-conv-input"
                value={newChipName}
                onChange={(e) => setNewChipName(e.target.value)}
                placeholder="Looking away"
                maxLength={64}
              />
            </label>
            <label className="agent-conv-field">
              <div className="agent-conv-field-head">
                <div>
                  <b>
                    Observation kind <i className="agent-perc-req">*</i>
                  </b>
                </div>
              </div>
              <Select
                searchable={false}
                value={newChipKind}
                onChange={(v) => setNewChipKind((v ?? newChipKind) as PerceptionChipKind)}
                options={PERCEPTION_CHIP_KINDS.map((k) => ({
                  value: k.id,
                  label: k.label,
                }))}
              />
              <span className="agent-perc-field-hint">
                Choose where this chip appears in observations. Available kinds are managed by Pulse.
              </span>
            </label>
            <label className="agent-conv-field">
              <div className="agent-conv-field-head">
                <div>
                  <b>Description</b>
                  <span>Optional</span>
                </div>
              </div>
              <textarea
                className="agent-conv-textarea agent-conv-textarea--sm"
                value={newChipDesc}
                onChange={(e) => setNewChipDesc(e.target.value)}
                placeholder="Apply when the user's gaze repeatedly shifts away from the camera."
                rows={3}
                maxLength={240}
              />
              <span className="agent-perc-field-hint">
                Describe the observable cue that should trigger this chip.
              </span>
            </label>
          </div>
          <DialogFooter className="gap-2 sm:justify-end">
            <button type="button" className="agent-edit-btn" onClick={() => setAddChipOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="agent-edit-btn agent-edit-btn--primary"
              disabled={!newChipName.trim()}
              onClick={addCustomChip}
            >
              Add chip
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(ruleEdit)} onOpenChange={(o) => !o && setRuleEdit(null)}>
        <DialogContent className="bg-white sm:max-w-md" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{ruleEdit?.id ? "Edit rule" : "Add rule"}</DialogTitle>
            <DialogDescription>
              Short guidance applied with What to notice when writing visual observations.
            </DialogDescription>
          </DialogHeader>
          <label className="agent-conv-field">
            <div className="agent-conv-field-head">
              <div>
                <b>Rule</b>
              </div>
            </div>
            <textarea
              className="agent-conv-textarea agent-conv-textarea--sm"
              value={ruleEdit?.text || ""}
              maxLength={RULE_MAX}
              rows={3}
              onChange={(e) => setRuleEdit((r) => (r ? { ...r, text: e.target.value } : r))}
              placeholder="Describe only visible, observable cues."
            />
          </label>
          <DialogFooter className="gap-2 sm:justify-end">
            <button type="button" className="agent-edit-btn" onClick={() => setRuleEdit(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="agent-edit-btn agent-edit-btn--primary"
              disabled={!ruleEdit?.text.trim()}
              onClick={() => {
                if (!ruleEdit) return;
                const text = ruleEdit.text.trim();
                if (ruleEdit.id) {
                  writeRules(rules.map((r) => (r.id === ruleEdit.id ? { ...r, text } : r)));
                } else {
                  writeRules([
                    ...rules,
                    { id: `rule-${Date.now()}`, text, enabled: true },
                  ]);
                }
                setRuleEdit(null);
              }}
            >
              {ruleEdit?.id ? "Save rule" : "Add rule"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(ruleDeleteId)} onOpenChange={(o) => !o && setRuleDeleteId(null)}>
        <DialogContent className="bg-white sm:max-w-md" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Delete rule?</DialogTitle>
            <DialogDescription>
              {(() => {
                const text = rules.find((r) => r.id === ruleDeleteId)?.text?.trim();
                return text
                  ? `Remove “${text.length > 120 ? `${text.slice(0, 120)}…` : text}” from this agent’s Perception rules.`
                  : "Remove this rule from Perception.";
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <button type="button" className="agent-edit-btn" onClick={() => setRuleDeleteId(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="agent-edit-btn agent-edit-btn--danger"
              onClick={() => {
                if (!ruleDeleteId) return;
                writeRules(rules.filter((x) => x.id !== ruleDeleteId));
                setRuleDeleteId(null);
              }}
            >
              Delete rule
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
